import React, { useRef } from 'react';
import { cn } from '../../../util';
export type ButtonColor = 'midnight' | 'error';
type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
type ButtonSize = 'xsmall' | 'small' | 'medium' | 'large';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  children: React.ReactNode;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  rippling?: boolean;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>
  ) => void;
}

const getButtonStyles = ({
  color = 'midnight',
  variant = 'primary',
  size = 'large',
  rippling = false,
  className
}: Pick<
  ButtonProps,
  'color' | 'variant' | 'size' | 'leadingIcon' | 'trailingIcon' | 'rippling' | 'className'
>) => {
  return cn(
    // Base styles
    'flex gap-2 items-center justify-center font-medium z-10 relative overflow-hidden transition-colors border border-solid rounded-lg cursor-pointer disabled:cursor-default focus-visible:outline-dashed outline-2 outline-offset-2 outline-heaven-600',

    // Size
    {
      'py-1 px-2 text-xs font-normal': size === 'xsmall',
      'py-[5px] px-3 text-base': size === 'small',
      'py-[7px] px-4 text-base': size === 'medium',
      'py-[11px] px-6 text-base': size === 'large'
    },

    // Variant + Color combinations
    {
      'border-transparent': variant === 'primary' || variant === 'tertiary',
      'text-white disabled:bg-grey-400': variant === 'primary',
      'bg-transparent disabled:bg-transparent disabled:text-grey-400 disabled:border-grey-400 hover:bg-grey-200 active:bg-grey-200':
        variant === 'secondary',
      'font-normal bg-transparent disabled:text-grey-400 disabled:bg-transparent hover:bg-grey-200 active:bg-grey-200':
        variant === 'tertiary',

      // Ripple effect
      '!bg-primary-dark': rippling === true && color === 'midnight' && variant === 'primary',
      '!bg-grey-200':
        rippling === true &&
        color === 'midnight' &&
        (variant === 'secondary' || variant === 'tertiary'),

      // Midnight
      'bg-primary-main hover:bg-primary-dark active:bg-primary-dark':
        color === 'midnight' && variant === 'primary',
      'text-primary-main border-primary-main hover:text-primary-dark hover:border-primary-dark active:text-primary-dark active:border-primary-dark':
        color === 'midnight' && variant === 'secondary',
      'text-primary-main hover:text-primary-dark active:text-primary-dark':
        color === 'midnight' && variant === 'tertiary',

      // Error
      'bg-error-main hover:bg-error-dark active:bg-error-dark':
        color === 'error' && variant === 'primary',
      'text-error-main border-error-main hover:text-error-dark hover:border-error-dark active:text-error-dark active:border-error-dark':
        color === 'error' && variant === 'secondary',
      'text-error-main  hover:text-error-dark active:text-error-dark':
        color === 'error' && variant === 'tertiary'
    },
    className
  );
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    color = 'midnight',
    variant = 'primary',
    size = 'large',
    leadingIcon,
    trailingIcon,
    children,
    onClick,
    ...props
  }) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    const createRipple = (
      event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>
    ) => {
      const button = buttonRef.current;
      if (!button) return;

      const circle = document.createElement('span');
      const rect = button.getBoundingClientRect();
      const rippleColor = variant === 'primary' ? 'bg-white' : 'bg-grey-600';

      const size = Math.max(button.clientWidth, button.clientHeight);
      circle.style.width = circle.style.height = `${size}px`;
      circle.className = `absolute rounded-full transform scale-0 animate-ripple pointer-events-none ${rippleColor}`;

      if ('clientX' in event && 'clientY' in event) {
        const x = event.clientX - rect.left - size / 2;
        const y = event.clientY - rect.top - size / 2;
        circle.style.left = `${x}px`;
        circle.style.top = `${y}px`;
      }

      button.appendChild(circle);

      setTimeout(() => {
        circle.remove();
      }, 500);
    };

    return (
      <button
        className={getButtonStyles({
          variant,
          color,
          size,
          leadingIcon,
          trailingIcon,
          className
        })}
        onMouseDown={(e) => {
          createRipple(e);
        }}
        onMouseUp={(e) => {
          onClick?.(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            createRipple(e);
          }
        }}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            onClick?.(e);
          }
        }}
        ref={buttonRef}
        {...props}
      >
        {leadingIcon ?? leadingIcon}
        {children}
        {trailingIcon ?? trailingIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
